/**
 * Production-Ready - DateTable (Builder Pattern)
 * Based on engine's pretty-print output structure
 * Backend integration pending (see docs/planning/bond-calculator-app-plan.md)
 */

import { Box, Button, HStack, Text } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import { TableWithHeader } from '~/components/tables/TableWithHeader';
import type { ProjectDateRecordByPaymentDate, ProjectDateRecordByYear } from '~/types';
import { formatDateForDisplay } from '~/utils/date-utils';
import type {
  DateRecord as BaseDateRecord,
  DateRecordsMapByPaymentDate,
  DateRecordsMapByTaxYear,
} from './DateBuilder';

// Extend DateRecord to satisfy ReusableTable constraint
interface DateRecord extends BaseDateRecord, Record<string, unknown> {}

// Support both data structures for flexibility
type DateRecordMap = {
  [key: number]: DateRecord;
};

interface DateTableProps {
  data?: DateRecord[];
  dateRecordMap?: DateRecordMap | null;
  // New props for the three date objects
  dateRecordsMapByTaxYear?: DateRecordsMapByTaxYear | null;
  dateRecordsMapByPaymentDate?: DateRecordsMapByPaymentDate | null;
  title?: string;
  isLoading?: boolean;
  error?: string | null;
  showSummary?: boolean;
  maxRows?: number;
}

// Timezone-safe utility function to format date
const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  try {
    // Use our timezone-safe utility function
    const formatted = formatDateForDisplay(dateString);
    return formatted;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// Utility function to format payment dates array
const formatPaymentDates = (paymentDates: string[]): string => {
  if (!paymentDates || paymentDates.length === 0) return '';

  const formatted = paymentDates.map((date) => formatDate(date)).join(', ');
  return formatted;
};

// Convert DateRecordMap to array format
const convertToTableData = (dateRecordMap: DateRecordMap): DateRecord[] => {
  return Object.values(dateRecordMap).sort((a, b) => a.taxYear - b.taxYear);
};

// Calculate summary statistics
const calculateSummary = (data: DateRecord[]) => {
  if (data.length === 0) return null;

  const firstYear = data[0];
  const lastYear = data[data.length - 1];

  return {
    startingTaxYear: firstYear.taxYear,
    endingTaxYear: lastYear.taxYear,
    yearSpan: data.length,
    paymentDatesPerYear: firstYear.paymentDates.length,
    fiscalYearPattern: firstYear.fiscalYearEndDate,
  };
};

export const DateTable: React.FC<DateTableProps> = ({
  data,
  dateRecordMap,
  dateRecordsMapByTaxYear,
  dateRecordsMapByPaymentDate,
  title = 'Date Information Timeline',
  isLoading = false,
  error = null,
  showSummary = false,
  maxRows = LAYOUT_SPACING.TABLE_DEFAULT_ROWS,
}) => {
  // Determine actual data to use for original date records
  const tableData = data || (dateRecordMap ? convertToTableData(dateRecordMap) : []);

  // Define column structure for original date records
  const dateRecordColumns: ColumnDefinition<DateRecord>[] = [
    {
      key: 'taxYear',
      header: 'Tax Year',
      accessor: 'taxYear',
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'paymentDates',
      header: 'Payment Dates',
      accessor: (item) => formatPaymentDates(item.paymentDates),
      headerTextAlign: 'left',
      cellTextAlign: 'left',
    },
    {
      key: 'fiscalYearEndDate',
      header: 'Fiscal Year End',
      accessor: (item) => formatDate(item.fiscalYearEndDate),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'lastPaymentDateOfFiscalYear',
      header: 'Last Payment',
      accessor: (item) => formatDate(item.lastPaymentDateOfFiscalYear),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
  ];

  // Define column structure for tax year map
  const taxYearColumns: ColumnDefinition<ProjectDateRecordByYear>[] = [
    {
      key: 'projectYear',
      header: 'Project Year',
      accessor: 'projectYear',
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'taxYear',
      header: 'Tax Year',
      accessor: 'taxYear',
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'paymentDates',
      header: 'Payment Dates',
      accessor: (item) => formatPaymentDates(item.paymentDates),
      headerTextAlign: 'left',
      cellTextAlign: 'left',
    },
    {
      key: 'fiscalYearEndDate',
      header: 'Fiscal Year End',
      accessor: (item) => formatDate(item.fiscalYearEndDate),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'lastPaymentDateOfFiscalYear',
      header: 'Last Payment',
      accessor: (item) => formatDate(item.lastPaymentDateOfFiscalYear),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
  ];

  // Define column structure for payment date map
  const paymentDateColumns: ColumnDefinition<ProjectDateRecordByPaymentDate>[] = [
    {
      key: 'paymentDate',
      header: 'Payment Date',
      accessor: (item) => formatDate(item.paymentDate),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'taxYear',
      header: 'Tax Year',
      accessor: 'taxYear',
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'fiscalYearEndDate',
      header: 'Fiscal Year End',
      accessor: (item) => formatDate(item.fiscalYearEndDate),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'isInterestOnly',
      header: 'Interest Only',
      accessor: (item) => (item.isInterestOnly ? 'Yes' : 'No'),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'isLastPaymentInFiscalYear',
      header: 'Last Payment',
      accessor: (item) => (item.isLastPaymentInFiscalYear ? 'Yes' : 'No'),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'revenueCollectionByDatePercentage',
      header: 'Revenue %',
      accessor: (item) => `${(item.revenueCollectionByDatePercentage * 100).toFixed(1)}%`,
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
  ];

  // State for active view - must be declared before any early returns (React hooks rule)
  const [activeView, setActiveView] = useState<'original' | 'taxYear' | 'paymentDate'>('original');

  // Prepare data for tax year map
  const taxYearData = dateRecordsMapByTaxYear
    ? Object.values(dateRecordsMapByTaxYear).sort((a, b) => a.taxYear - b.taxYear)
    : [];

  // Prepare data for payment date map
  const paymentDateData = dateRecordsMapByPaymentDate
    ? Object.values(dateRecordsMapByPaymentDate).sort(
        (a, b) => a.taxYear - b.taxYear || a.paymentDate.localeCompare(b.paymentDate)
      )
    : [];

  // Check if we have any data to display - must be before early returns
  const hasOriginalData = tableData && tableData.length > 0;
  const hasTaxYearData = taxYearData.length > 0;
  const hasPaymentDateData = paymentDateData.length > 0;

  // Determine which views are available - must be before early returns
  const availableViews = useMemo<('original' | 'taxYear' | 'paymentDate')[]>(() => {
    const views: ('original' | 'taxYear' | 'paymentDate')[] = [];
    if (hasOriginalData) views.push('original');
    if (hasTaxYearData) views.push('taxYear');
    if (hasPaymentDateData) views.push('paymentDate');
    return views;
  }, [hasOriginalData, hasTaxYearData, hasPaymentDateData]);

  // Set initial view to first available - must be before early returns
  React.useEffect(() => {
    if (availableViews.length > 0 && !availableViews.includes(activeView)) {
      setActiveView(availableViews[0] as typeof activeView);
    }
  }, [activeView, availableViews]);

  // Handle loading state - must be after hooks
  if (isLoading) {
    return (
      <Box>
        <Text fontSize="md" fontWeight="semibold" mb={4}>
          {title}
        </Text>
        <Text color="gray.500">Loading date information...</Text>
      </Box>
    );
  }

  // Handle error state - must be after hooks
  if (error) {
    return (
      <Box>
        <Text fontSize="md" fontWeight="semibold" mb={4}>
          {title}
        </Text>
        <Text color="red.500">Error: {error}</Text>
      </Box>
    );
  }

  if (!hasOriginalData && !hasTaxYearData && !hasPaymentDateData) {
    return (
      <Box>
        <Text fontSize="md" fontWeight="semibold" mb={4}>
          {title}
        </Text>
        <Text color="gray.500">No date information available</Text>
      </Box>
    );
  }

  // Calculate summary if requested
  const summary = showSummary && hasOriginalData ? calculateSummary(tableData) : null;

  return (
    <Box height="100%" display="flex" flexDirection="column" minHeight={0}>
      {/* Summary section */}
      {summary && (
        <Box mb={4} p={4} flexShrink={0}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Summary
          </Text>
          <Text fontSize="sm">
            <strong>Starting Tax Year:</strong> {summary.startingTaxYear}
          </Text>
          <Text fontSize="sm">
            <strong>Ending Tax Year:</strong> {summary.endingTaxYear}
          </Text>
          <Text fontSize="sm">
            <strong>Year Span:</strong> {summary.yearSpan} years
          </Text>
          <Text fontSize="sm">
            <strong>Payment Dates per Year:</strong> {summary.paymentDatesPerYear}
          </Text>
          <Text fontSize="sm">
            <strong>Fiscal Year Pattern:</strong> {formatDate(summary.fiscalYearPattern)}
          </Text>
        </Box>
      )}

      {/* Button interface for different date structures */}
      {availableViews.length > 1 && (
        <HStack mb={4} gap={2} flexShrink={0}>
          {hasOriginalData && (
            <Button
              size="sm"
              colorPalette={activeView === 'original' ? 'blue' : 'gray'}
              variant={activeView === 'original' ? 'solid' : 'outline'}
              onClick={() => setActiveView('original')}
            >
              Original Date Records
            </Button>
          )}
          {hasTaxYearData && (
            <Button
              size="sm"
              colorPalette={activeView === 'taxYear' ? 'blue' : 'gray'}
              variant={activeView === 'taxYear' ? 'solid' : 'outline'}
              onClick={() => setActiveView('taxYear')}
            >
              Tax Year Map
            </Button>
          )}
          {hasPaymentDateData && (
            <Button
              size="sm"
              colorPalette={activeView === 'paymentDate' ? 'blue' : 'gray'}
              variant={activeView === 'paymentDate' ? 'solid' : 'outline'}
              onClick={() => setActiveView('paymentDate')}
            >
              Payment Date Map
            </Button>
          )}
        </HStack>
      )}

      {/* Content based on active view */}
      <Box flex={1} minHeight={0} display="flex" flexDirection="column">
        {activeView === 'original' && hasOriginalData && (
          <TableWithHeader title={title} totalRows={tableData.length} maxRows={maxRows}>
            <ReusableTable columns={dateRecordColumns} data={tableData} />
          </TableWithHeader>
        )}

        {activeView === 'taxYear' && hasTaxYearData && (
          <TableWithHeader title={title} totalRows={taxYearData.length} maxRows={maxRows}>
            <ReusableTable columns={taxYearColumns} data={taxYearData} />
          </TableWithHeader>
        )}

        {activeView === 'paymentDate' && hasPaymentDateData && (
          <TableWithHeader title={title} totalRows={paymentDateData.length} maxRows={maxRows}>
            <ReusableTable columns={paymentDateColumns} data={paymentDateData} />
          </TableWithHeader>
        )}
      </Box>
    </Box>
  );
};

// Export types for use in other files
export type { DateRecordMap };
